import React, { useState, useEffect } from 'react';
import { Clock, MapPin, LogIn, LogOut, CheckCircle, AlertCircle } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useTimeEntries } from '../hooks/useTimeEntries';
import { usePayPeriodSettings } from '../hooks/usePayPeriodSettings';
import { getCurrentPayPeriod } from '../utils/payrollPeriodCalculator';
import type { TimeEntry } from '../types';

const TimeClock: React.FC = () => {
  const { user } = useAuth();
  const { settings: payPeriodSettings } = usePayPeriodSettings();
  const [location, setLocation] = useState<{ latitude: number; longitude: number } | null>(null);
  const [locationError, setLocationError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [notes, setNotes] = useState('');

  const { timeEntries, clockIn, clockOut, getActiveEntry, refetch } = useTimeEntries(user?.employeeId);
  const activeEntry = user?.employeeId ? getActiveEntry(user.employeeId) : null;

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          });
          setLocationError(null);
        },
        (error) => {
          setLocationError('Unable to get your location. Clock in/out will proceed without location data.');
          console.error('Geolocation error:', error);
        }
      );
    } else {
      setLocationError('Geolocation is not supported by your browser.');
    }
  }, []);

  const handleClockIn = async () => {
    if (!user?.employeeId) {
      setError('Employee ID not found. Please contact your administrator.');
      console.error('Clock in failed: No employee ID', { user });
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      if (!payPeriodSettings) {
        throw new Error('Pay period settings not configured');
      }

      const currentPeriod = getCurrentPayPeriod(
        payPeriodSettings.frequency,
        payPeriodSettings.periodStart,
        payPeriodSettings.payday
      );

      console.log('Attempting clock in with:', {
        employeeId: user.employeeId,
        location,
        period: currentPeriod
      });

      await clockIn(
        user.employeeId,
        location?.latitude || null,
        location?.longitude || null,
        currentPeriod.start,
        currentPeriod.end
      );

      setSuccess('Clocked in successfully!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Clock in error:', err);
      setError(err instanceof Error ? err.message : 'Failed to clock in');
    } finally {
      setLoading(false);
    }
  };

  const handleClockOut = async () => {
    if (!activeEntry) return;

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await clockOut(
        activeEntry.id,
        location?.latitude || null,
        location?.longitude || null,
        notes || undefined
      );

      setSuccess('Clocked out successfully!');
      setNotes('');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to clock out');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const calculateElapsedTime = (clockInTime: string) => {
    const start = new Date(clockInTime);
    const diff = Math.floor((currentTime.getTime() - start.getTime()) / 1000);
    const hours = Math.floor(diff / 3600);
    const minutes = Math.floor((diff % 3600) / 60);
    const seconds = diff % 60;

    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-8 text-white">
          <div className="flex items-center justify-center mb-4">
            <Clock className="h-16 w-16" />
          </div>
          <h2 className="text-3xl font-bold text-center mb-2">Time Clock</h2>
          <p className="text-center text-blue-100 text-lg">{formatTime(currentTime)}</p>
          <p className="text-center text-blue-200 text-sm mt-1">{formatDate(currentTime)}</p>
        </div>

        <div className="p-6">
          {success && (
            <div className="mb-4 rounded-lg bg-green-50 p-4 flex items-start">
              <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 mr-3 flex-shrink-0" />
              <div className="text-sm text-green-700">{success}</div>
            </div>
          )}

          {error && (
            <div className="mb-4 rounded-lg bg-red-50 p-4 flex items-start">
              <AlertCircle className="h-5 w-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
              <div className="text-sm text-red-700">{error}</div>
            </div>
          )}

          {locationError && (
            <div className="mb-4 rounded-lg bg-yellow-50 p-4 flex items-start">
              <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5 mr-3 flex-shrink-0" />
              <div className="text-sm text-yellow-700">{locationError}</div>
            </div>
          )}

          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center text-sm text-gray-600 mb-2">
              <MapPin className="h-4 w-4 mr-2" />
              <span className="font-medium">Location Status:</span>
            </div>
            {location ? (
              <p className="text-sm text-green-600 ml-6">
                Location detected ({location.latitude.toFixed(6)}, {location.longitude.toFixed(6)})
              </p>
            ) : (
              <p className="text-sm text-gray-500 ml-6">
                Detecting location...
              </p>
            )}
          </div>

          {activeEntry ? (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-green-800">Currently Clocked In</span>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    Active
                  </span>
                </div>
                <p className="text-sm text-green-700 mb-1">
                  Started: {new Date(activeEntry.clockInTime).toLocaleString()}
                </p>
                <p className="text-2xl font-bold text-green-900 mt-2">
                  {calculateElapsedTime(activeEntry.clockInTime)}
                </p>
                <p className="text-xs text-green-600 mt-1">Hours Elapsed</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notes (Optional)
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add any notes about your shift..."
                  rows={3}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                />
              </div>

              <button
                onClick={handleClockOut}
                disabled={loading}
                className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-red-600 text-white rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-lg font-semibold"
              >
                <LogOut className="h-6 w-6" />
                {loading ? 'Clocking Out...' : 'Clock Out'}
              </button>
            </div>
          ) : (
            <button
              onClick={handleClockIn}
              disabled={loading}
              className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-lg font-semibold"
            >
              <LogIn className="h-6 w-6" />
              {loading ? 'Clocking In...' : 'Clock In'}
            </button>
          )}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Time Entries</h3>
        <div className="space-y-3">
          {timeEntries.slice(0, 5).map((entry) => (
            <div
              key={entry.id}
              className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
            >
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900">
                  {new Date(entry.clockInTime).toLocaleDateString()}
                </p>
                <p className="text-xs text-gray-500">
                  {new Date(entry.clockInTime).toLocaleTimeString()} - {' '}
                  {entry.clockOutTime ? new Date(entry.clockOutTime).toLocaleTimeString() : 'In Progress'}
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm font-semibold text-gray-900">
                  {entry.hoursWorked ? `${entry.hoursWorked.toFixed(2)} hrs` : '--'}
                </p>
                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                  entry.status === 'approved' ? 'bg-green-100 text-green-800' :
                  entry.status === 'rejected' ? 'bg-red-100 text-red-800' :
                  'bg-yellow-100 text-yellow-800'
                }`}>
                  {entry.status}
                </span>
              </div>
            </div>
          ))}
          {timeEntries.length === 0 && (
            <p className="text-sm text-gray-500 text-center py-4">No time entries yet</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default TimeClock;
